import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Play, Square, Copy, Loader2, AlertTriangle, Timer } from "lucide-react";
import { api, errMsg, isPaidRoute } from "@/lib/api";
import { streamSSE } from "@/lib/sse";
import { PageHeader, FreeBadge } from "@/components/Common";

const copyText = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Kopiert"), () => toast.error("Kopieren nicht möglich"));

const TargetRow = ({ idx, t, providers, models, onChange, onRemove }) => (
  <div className="flex flex-col gap-2 rounded-lg border border-white/5 p-3 sm:flex-row sm:items-center" data-testid={`compare-target-${idx}`}>
    <select data-testid={`compare-provider-${idx}`} className="field sm:w-56" value={t.pid} onChange={(e) => onChange({ pid: e.target.value, model: "" })}>
      <option value="">Provider wählen</option>
      {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
    <select data-testid={`compare-model-${idx}`} className="field flex-1" value={t.model} disabled={!t.pid} onChange={(e) => onChange({ ...t, model: e.target.value })}>
      <option value="">Modell wählen</option>
      {(models[t.pid] || []).map((m) => <option key={m.id} value={m.id}>{m.id}{m.free ? "" : " · kostenpflichtig"}</option>)}
    </select>
    {t.pid && t.model && <FreeBadge free={!isPaidRoute(providers.find((p) => p.id === t.pid), t.model)} />}
    <button onClick={onRemove} data-testid={`compare-remove-${idx}`} className="btn-ghost p-2" aria-label="Entfernen"><X className="h-4 w-4" /></button>
  </div>
);

const ResultCard = ({ target, r, elapsed }) => (
  <article className={`glass flex min-h-[220px] flex-col p-5 ${r?.status === "error" ? "border-red-500/40" : ""}`} data-testid={`compare-result-${target.index}`}>
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className="font-mono text-xs text-slate-300 break-all">{target.target}</div>
      <span data-testid={`compare-status-${target.index}`} className={`shrink-0 text-xs font-semibold ${!r ? "text-blue-300" : r.status === "ok" ? "text-emerald-300" : "text-red-300"}`}>
        {!r ? "läuft…" : r.status === "ok" ? "OK" : r.error?.code}
      </span>
    </div>
    {!r && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />{elapsed}s</div>}
    {r?.status === "ok" && <div className="flex-1 whitespace-pre-wrap text-sm text-slate-100" data-testid={`compare-content-${target.index}`}>{r.content}</div>}
    {r?.status === "error" && <div className="flex items-start gap-2 text-sm text-red-200"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{r.error?.message}</div>}
    {r && (
      <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-2 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3" />{r.duration_ms} ms</span>
        {r.route && <span>{r.route.provider_name}</span>}
        {r.route && <FreeBadge free={r.route.free} />}
        {r.status === "ok" && <button onClick={() => copyText(r.content)} data-testid={`compare-copy-${target.index}`} className="ml-auto text-slate-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>}
      </div>
    )}
  </article>
);

export default function Compare() {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState({});
  const [targets, setTargets] = useState([{ pid: "", model: "" }, { pid: "", model: "" }]);
  const [prompt, setPrompt] = useState("");
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [run, setRun] = useState(null); // {targets, results, startedAt}
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/catalog"), api.get("/credentials")]).then(([c, k]) => {
      const ids = new Set(k.data.map((x) => x.provider_id));
      setProviders(c.data.providers.filter((p) => ids.has(p.id) && p.enabled));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    targets.forEach((t) => {
      if (t.pid && !models[t.pid]) {
        setModels((m) => ({ ...m, [t.pid]: [] }));
        api.get(`/providers/${t.pid}/models`).then((r) => setModels((m) => ({ ...m, [t.pid]: r.data.models }))).catch((e) => toast.error(errMsg(e)));
      }
    });
  }, [targets, models]);
  useEffect(() => { if (!busy) return undefined; const id = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(id); }, [busy]);

  const ready = targets.filter((t) => t.pid && t.model);
  const anyPaid = useMemo(() => ready.some((t) => isPaidRoute(providers.find((p) => p.id === t.pid), t.model)), [ready, providers]);

  const start = async () => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true); setTick(0);
    setRun({ targets: [], results: {}, startedAt: Date.now() });
    try {
      await api.get("/auth/me");
      for await (const ev of streamSSE("/gateway/compare", { prompt, targets: ready.map((t) => `${t.pid}:${t.model}`), allow_paid: anyPaid && confirmPaid }, ctrl.signal)) {
        if (ev.type === "start") setRun((r) => ({ ...r, targets: ev.targets }));
        if (ev.type === "result") setRun((r) => ({ ...r, results: { ...r.results, [ev.index]: ev } }));
      }
    } catch (e) {
      if (e.name === "AbortError") toast.info("Vergleich abgebrochen");
      else toast.error(`${e.code ? e.code + ": " : ""}${e.message}`);
    } finally { setBusy(false); abortRef.current = null; }
  };

  return (
    <div data-testid="compare-page">
      <PageHeader title="Modellvergleich" subtitle="Ein Prompt läuft parallel gegen 2–4 Modelle (max. 3 gleichzeitig, 90 s Timeout pro Modell). Ergebnisse erscheinen, sobald sie eintreffen; Fehler eines Providers stoppen die anderen nicht. Jedes Modell verbraucht eine Anfrage deines Tageslimits." />
      <section className="glass space-y-3 p-6">
        {providers.length === 0 && <p className="text-sm text-amber-300" data-testid="compare-no-providers">Noch kein Provider eingerichtet. <Link to="/app/providers" className="underline">Jetzt einrichten</Link></p>}
        {targets.map((t, i) => (
          <TargetRow key={i} idx={i} t={t} providers={providers} models={models}
            onChange={(v) => setTargets(targets.map((x, k) => (k === i ? v : x)))} onRemove={() => setTargets(targets.filter((_, k) => k !== i))} />
        ))}
        {targets.length < 4 && <button onClick={() => setTargets([...targets, { pid: "", model: "" }])} data-testid="compare-add-target" className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-200"><Plus className="h-4 w-4" />Modell hinzufügen</button>}
        <textarea data-testid="compare-prompt-input" rows={4} className="field" placeholder="Gemeinsamer Prompt für alle Modelle…" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <div className="text-xs text-slate-400" data-testid="compare-cost-status">Kostenstatus vor dem Start: {ready.length === 0 ? "keine Modelle gewählt" : anyPaid ? <span className="text-red-300">enthält kostenpflichtige Modelle</span> : <span className="text-emerald-300">alle {ready.length} Modelle kostenlos/lokal</span>}</div>
        {anyPaid && (
          <label className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="compare-paid-warning">
            <input type="checkbox" checked={confirmPaid} onChange={(e) => setConfirmPaid(e.target.checked)} data-testid="compare-confirm-paid" className="mt-0.5 accent-red-500" />
            Mindestens ein Modell ist kostenpflichtig (zusätzlich ist eine Admin-Freigabe nötig). Ich bestätige die Nutzung.
          </label>
        )}
        <div className="flex gap-3">
          {busy ? (
            <button onClick={() => abortRef.current?.abort()} data-testid="compare-abort-button" className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-red-200"><Square className="h-4 w-4" />Abbrechen</button>
          ) : (
            <button onClick={start} disabled={ready.length < 2 || ready.length !== targets.length || !prompt.trim() || (anyPaid && !confirmPaid)} data-testid="compare-start-button" className="btn-grad inline-flex items-center gap-2 px-5 py-2.5"><Play className="h-4 w-4" />Vergleich starten</button>
          )}
        </div>
      </section>
      {run && (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="compare-results">
          {run.targets.map((t) => <ResultCard key={t.index} target={t} r={run.results[t.index]} elapsed={tick} />)}
        </div>
      )}
    </div>
  );
}
